import { useEffect, useRef, useState } from 'react';

function postCloseToParent() {
  window.parent.postMessage(JSON.stringify({ action: 'feedbackclose' }), '*');
}

export function useCopyShareLink(
  shareUrl: string | null,
  onClose?: () => void,
) {
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState(false);
  // React state updates aren't synchronous, so a state-only guard can't stop
  // a second click fired before the first `await` resolves — use a ref too.
  const copyingRef = useRef(false);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    };
  }, []);

  function handleClose() {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    if (onClose) {
      onClose();
      return;
    }
    postCloseToParent();
  }

  async function handleCopyLink() {
    if (!shareUrl || copyingRef.current) return;
    copyingRef.current = true;
    setCopyError(false);

    try {
      await navigator.clipboard.writeText(shareUrl);
    } catch {
      // Let the user retry instead of leaving Copy Link permanently disabled.
      copyingRef.current = false;
      setCopyError(true);
      return;
    }

    setCopied(true);
    // Give the user a moment to see the "Link Copied" confirmation before
    // the modal closes itself.
    closeTimeoutRef.current = setTimeout(() => {
      closeTimeoutRef.current = null;
      handleClose();
    }, 3000);
  }

  return { copied, copyError, handleClose, handleCopyLink };
}
